/**
 * Usage analytics: the tabs people open and the buttons they press, for the
 * usage dashboard. Posted to the portal's own /api/analytics, which adds who
 * and where; nothing here knows the person. Fire and forget, as a beacon, so a
 * press that leaves the page (sign out, Manage subscription) still arrives.
 *
 * A button is named by a fixed id (`<Button track="…">`), never by its label,
 * so a reworded label does not split its numbers. The server takes
 * [a-z][a-z0-9_]{1,47} (server/src/routes/usage.ts).
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router';
import { tabForHash } from '../pages/privacy/tabs.ts';

export type UsageTab = 'signature' | 'profile' | 'privacy/devices' | 'privacy/shared' | 'privacy/delete' | 'subscription';

type UsageEvent =
  | { event: 'tab_selected'; tab: UsageTab; via: 'tap' | 'load' }
  | { event: 'button_tap'; button: string; tab?: UsageTab };

const ENDPOINT = '/api/analytics';

/** The tab an address shows; null for one that is no tab (an old address on its way, an unknown page). */
export function tabOf(pathname: string, hash: string): UsageTab | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/' || path === '/signature') return 'signature';
  if (path === '/profile') return 'profile';
  if (path === '/privacy') return `privacy/${tabForHash(hash)}`;
  if (path === '/subscription') return 'subscription';
  return null;
}

/**
 * The id of a press in the signature editor, from the control's testID (Blue
 * Stamp's own names: copy-signature, section-you, add-network…):
 * `editor_<testID>`; null when there is nothing to name it by.
 */
export function editorButton(testId: string): string | null {
  const name = testId.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return name ? `editor_${name}`.slice(0, 48) : null;
}

function send(body: UsageEvent): void {
  const json = JSON.stringify(body);
  try {
    if (navigator.sendBeacon?.(ENDPOINT, new Blob([json], { type: 'application/json' }))) return;
  } catch {
    // Falls through to fetch.
  }
  void fetch(ENDPOINT, {
    method: 'POST',
    credentials: 'same-origin',
    keepalive: true,
    headers: { 'content-type': 'application/json' },
    body: json,
  }).catch(() => undefined);
}

/** The tab on screen, which a button press is counted under. */
let currentTab: UsageTab | null = null;

export function buttonTapped(button: string): void {
  send(currentTab ? { event: 'button_tap', button, tab: currentTab } : { event: 'button_tap', button });
}

/**
 * Sends `tab_selected` each time the tab on screen changes, once `enabled` (the
 * session is known). `tap` when the person moved there (a link, the navigation,
 * a Privacy tab); `load` when the page opened there (landing, an address, a
 * redirect from an old one, Back and Forward).
 */
export function useUsageTabs(enabled: boolean): void {
  const location = useLocation();
  const navigationType = useNavigationType();
  const tab = tabOf(location.pathname, location.hash);
  const last = useRef<UsageTab | null>(null);

  useEffect(() => {
    currentTab = tab;
    if (!enabled || !tab || tab === last.current) return;
    last.current = tab;
    send({ event: 'tab_selected', tab, via: navigationType === 'PUSH' ? 'tap' : 'load' });
  }, [enabled, tab, navigationType]);
}
